import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../services/api';

interface ActivityActor {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  email: string;
}

interface ActivityLogItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  entityTitle?: string;
  details?: Record<string, unknown>;
  createdAt: string;
  actor: ActivityActor | null;
}

interface ActivityLogsResponse {
  items: ActivityLogItem[];
  total: number;
  page: number;
  limit: number;
}

const actionLabels: Record<string, string> = {
  create: 'Создание',
  update: 'Изменение',
  delete: 'Удаление',
  status_change: 'Смена статуса',
};

const entityLabels: Record<string, string> = {
  article: 'Статья',
  issue: 'Выпуск',
  news: 'Новость',
  user: 'Пользователь',
};

function actorName(actor: ActivityActor | null): string {
  if (!actor) return 'Неизвестный пользователь';
  return `${actor.lastName} ${actor.firstName}${actor.middleName ? ` ${actor.middleName}` : ''}`.trim();
}

export const ActivityLogTab: React.FC = () => {
  const [items, setItems] = useState<ActivityLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 25;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (entityType) params.set('entityType', entityType);
      if (action) params.set('action', action);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());

      const response = await api.get<ActivityLogsResponse>(`/admin/activity-logs?${params.toString()}`);
      setItems(response.data.items);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось загрузить журнал');
    } finally {
      setLoading(false);
    }
  }, [page, entityType, action, searchQuery]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Журнал действий
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Поиск по объекту, ФИО, email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 260, flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Тип объекта</InputLabel>
          <Select
            label="Тип объекта"
            value={entityType}
            onChange={(e) => {
              setPage(1);
              setEntityType(e.target.value);
            }}
          >
            <MenuItem value="">Все</MenuItem>
            <MenuItem value="article">Статья</MenuItem>
            <MenuItem value="issue">Выпуск</MenuItem>
            <MenuItem value="news">Новость</MenuItem>
            <MenuItem value="user">Пользователь</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Действие</InputLabel>
          <Select
            label="Действие"
            value={action}
            onChange={(e) => {
              setPage(1);
              setAction(e.target.value);
            }}
          >
            <MenuItem value="">Все</MenuItem>
            <MenuItem value="create">Создание</MenuItem>
            <MenuItem value="update">Изменение</MenuItem>
            <MenuItem value="delete">Удаление</MenuItem>
            <MenuItem value="status_change">Смена статуса</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Когда</TableCell>
                  <TableCell>Кто</TableCell>
                  <TableCell>Действие</TableCell>
                  <TableCell>Объект</TableCell>
                  <TableCell>Детали</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Записей пока нет
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleString('ru-RU')}
                      </TableCell>
                      <TableCell>
                        {actorName(item.actor)}
                        {item.actor?.email ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {item.actor.email}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{actionLabels[item.action] || item.action}</TableCell>
                      <TableCell>
                        {entityLabels[item.entityType] || item.entityType}
                        {item.entityTitle ? `: ${item.entityTitle}` : ''}
                      </TableCell>
                      <TableCell>
                        {item.details && Object.keys(item.details).length > 0
                          ? JSON.stringify(item.details)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ActivityLogTab;
